import Icon, { ICONS, COLORS } from "@general-ui/icon";
import classNames from "classnames";
import { UIEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTypedSelector, useAppDispatch } from "store/reducers/use-typed-selector";
import { OptionalComponent } from "utils/optional-component";
import SuperAdminNotifications from "./components/super-admin-notifications";
import TabHeader from "@general-ui/tab-header/tab-header";
import { Case, SwitchCase } from "@general-ui/switch-case/switch-case";
import ActivityLog from "./components/activity-log";
import GradientText from "@general-ui/gradient-text/gradient-text";
import ActivityModal from "./components/activity-modal";
import { toMap } from "utils/utils";
import { BLAdmin } from "@brandlive/client-auth/lib/types";
import { EPermissions, FeatureFlagsEnum, IReturnableActivity } from "types/working-model";
import { useEventUuidParam } from "utils/use-event-uuid-param";
import { LoadActivityLog } from "connection/activity-log";
import { selectCurrentChannel } from "store/selectors/channel-flags";
import { hasMoreToScroll } from "./components/utils";
import { selectEditorNavV3, selectFeatureFlag } from "store/selectors/feature-flags";
import './admin-notifications.scss';
import { throttle } from "underscore";
import db from 'utils/datastore/indexed-db';
import { useHasPermission } from "@admin/authentication/AuthWrapper";
import { getAdmins } from "store/actions/admin/bl-admin";
import { useProjectDetailsEnabled } from "@admin/events/hooks/project-details";
import { useIsMounted } from "@/hooks/use-is-mounted";

interface ActivityLogParams {
	pageSize: number;
	published: boolean;
	cursor?: number;
	offset?: number;
}

const CLOSED_PAGE_SIZE = 10;
const OPEN_PAGE_SIZE = 30;

export default function AdminNotifications() {
	const superAdminNotifications = useTypedSelector(state =>
		state.ChannelAdminReducer.superAdminNotifications
	);
	const token = useTypedSelector(state => state.AuthReducer.token);
	const admins = useTypedSelector(state => state.ChannelAdminReducer.admins);
	const isLoadingWorkingEvent = useTypedSelector(state => state.CreateEventReducer.isLoadingWorkingEvent);
	const currentChannel = useTypedSelector(selectCurrentChannel);
	const activityLogEnabled = useTypedSelector(selectFeatureFlag(FeatureFlagsEnum.activity_log));
	const projectDetailsFlagIsOn = useProjectDetailsEnabled();
	const isEditorV3 = useTypedSelector(selectEditorNavV3);

	const [currentTab, setCurrentTab] = useState("Updates");
	const [activityLog, setActivityLog] = useState<IReturnableActivity[]>([]);
	const [activityLogCursor, setActivityLogCursor] = useState<number | undefined>();
	const [loadingActivityLog, setLoadingActivityLog] = useState(false);
	const [activityLogParams, setActivityLogParams] = useState<ActivityLogParams>({
		pageSize: CLOSED_PAGE_SIZE,
		published: true,
	});
	const [allNotificationsModalOpen, setAllNotificationsModalOpen] = useState(false);
	const [brandliveNotificationsOpen, setBrandliveNotificationsOpen] = useState(false);
	const [seenNotificationCount, setSeenNotificationCount] = useState(0);
	const brandliveNotificationsRef = useRef<HTMLDivElement | null>(null);
	const initialResultsRef = useRef<IReturnableActivity[] | undefined>(undefined);
	const adminLoadAttempted = useRef(false);
	const endReached = useRef(false);
	const pageSize = useRef(10);
	const loadingActivityLogRef = useRef(false);
	const eventUuid = useEventUuidParam();
	const activityModalBody = useRef<HTMLDivElement | null>(null);
	const canAccessLog = useHasPermission({
		blockedRoles: [
			EPermissions.Analytics,
			EPermissions.Producer,
			EPermissions.Moderation,
			EPermissions.PasscodeManager
		],
		anyExcept: true
	});
	const dispatch = useAppDispatch();
	const isMounted = useIsMounted();
	const showActivityLog = activityLogEnabled && !!eventUuid && canAccessLog;

	const adminMap = useMemo(() => {
		return toMap('id', admins) as Map<number, BLAdmin>;
	}, [admins]);

	const toggleNotificationsDropdown = async (e: React.MouseEvent) => {
		setBrandliveNotificationsOpen(prev => !prev);
		try {
			const seenNotes = superAdminNotifications.map(note => note.uuid);
			const seenActivities = Math.max(activityLog.length, seenNotificationCount);
			await db.putItem({ category: 'notification-counts', uuid: 'notifications', data: seenNotes, type: 'global' });
			await db.putItem({ category: 'notification-counts', uuid: eventUuid, data: seenActivities, type: 'activities' });
			setSeenNotificationCount(seenNotes.length + seenActivities);
		} catch (e) {
			console.error(e);
		}
	};

	const loadMore = useCallback((cursor: number | undefined) => {
		if (!cursor) return;
		let offset: number | undefined;

		if (cursor === activityLogParams.cursor) {
			if (activityLogParams.offset) {
				// if there already is an offset we need it to grow by the page size
				offset = activityLogParams.offset + activityLogParams.pageSize;
			} else {
				// if not then we need to offset by the page size
				offset = activityLogParams.pageSize;
			}
		}

		setActivityLogParams({
			cursor,
			pageSize: pageSize.current,
			published: true,
			offset
		});
	}, [activityLogParams]);

	const onScrollEnd = useCallback(() => {
		if (loadingActivityLogRef.current) return;

		loadMore(activityLogCursor);
	}, [loadMore, activityLogCursor]);

	const openActivityLogModal = useCallback(() => {
		pageSize.current = OPEN_PAGE_SIZE;
		setAllNotificationsModalOpen(true);
		setActivityLogParams(params => ({
			...params,
			pageSize: pageSize.current,
			offset: undefined
		}));
	}, []);

	const closeActivityLogModal = useCallback(() => {
		pageSize.current = CLOSED_PAGE_SIZE;
		setAllNotificationsModalOpen(false);
	}, []);

	const handleScroll = throttle((e: UIEvent<HTMLDivElement>) => {
		const target = e.target as HTMLDivElement;
		if (target.scrollTop + target.clientHeight >= target.scrollHeight - 10) {
			onScrollEnd?.();
		}
	}, 200);

	// the admins list is not guaranteed to be loaded in all places where this 
	// component is in use. If it is not, load the admins list.
	useEffect(() => {
		if (!adminLoadAttempted.current && token && currentChannel?.channel && (!admins || !admins.length)) {
			// do not try to do this more than once in case the array of admins is just empty
			adminLoadAttempted.current = true;
			dispatch(getAdmins(token, currentChannel.channel));
		}
	}, [admins, currentChannel, dispatch, token]);

	useEffect(() => {
		if (!showActivityLog || isEditorV3) return;

		if (!eventUuid || !currentChannel?.channel || isLoadingWorkingEvent) {
			setActivityLog([]);
			return;
		}

		if (endReached.current) {
			return;
		}

		setLoadingActivityLog(true);
		loadingActivityLogRef.current = true;

		// the API will collapse rows so there might be more to load even if the response size is not
		// equal to the page size. The presense of a "next" cursor on the response will tell whether
		// or not there are more rows to load in.
		LoadActivityLog(currentChannel.channel, eventUuid, activityLogParams)
			.then(res => {
				if (!isMounted.current) return;

				const activities = res.activities;
				endReached.current = !res.next;

				if (activityLogParams.cursor) { // if there is a cursor this is NOT the intial load
					setActivityLogCursor(res.next);

					// append to existing list
					setActivityLog(prev => [...prev, ...activities]);

					// if there is more to scroll and we have not yet reached the end, load more 
					// rather than wait for a scroll event
					if (res.next && hasMoreToScroll(activityModalBody.current)) {
						loadMore(res.next);
					}
				} else {
					// this is the intial load, no cursor used, so replace the array rather than append
					setActivityLog(activities);
					setActivityLogCursor(res.next);

					// no need to re-fetch the top of the list for this event
					if (!initialResultsRef.current) {
						initialResultsRef.current = activities;
					}

					// there are more to load and the modal is not scrolled to the bottom yet
					// so load in more
					if (res.next && hasMoreToScroll(activityModalBody.current)) {
						loadMore(res.next);
					}
				}
			})
			.catch(console.error)
			.finally(() => {
				if (!isMounted.current) return;
				setLoadingActivityLog(false);
				loadingActivityLogRef.current = false;
			});

	}, [eventUuid, currentChannel?.channel, activityLogParams, loadMore, showActivityLog, isLoadingWorkingEvent, isEditorV3, isMounted]);

	useEffect(() => {
		function checkClick(e: MouseEvent) {
			if (
				e.target instanceof HTMLElement
				&& !e.target.closest('.brandlive-notification-button')
			) {
				setBrandliveNotificationsOpen(false);
			}
		}

		if (brandliveNotificationsOpen) {
			window.addEventListener('click', checkClick);
		}

		return () => {
			window.removeEventListener('click', checkClick);
		};
	}, [brandliveNotificationsOpen]);

	useEffect(() => {
		(async () => {
			try {
				if (!isMounted.current) return;

				const seenSupers = await db.getItem<string[]>('global', 'notifications');
				const seenProject = await db.getItem<number>('activities', eventUuid);
				const seenIncludedSupers = superAdminNotifications.filter(note => seenSupers?.includes(note.uuid)).length;
				setSeenNotificationCount(seenIncludedSupers + (seenProject || 0));

			} catch (e) {
				console.error(e);
			}
		})();
	}, [superAdminNotifications, eventUuid, isMounted]);

	const unseenCount = superAdminNotifications.length + activityLog.length - seenNotificationCount;
	const unseenDisplay = unseenCount > 9 ? ' ' : unseenCount;

	if (isEditorV3) {
		return <></>;
	}

	return (
		<OptionalComponent display={!!superAdminNotifications.length || !!activityLog.length}>
			<div
				className={classNames("brandlive-notification-button", { showing: brandliveNotificationsOpen })}
				ref={brandliveNotificationsRef}
				data-testid="brandlive-notification-button"
			>
				<button onClick={toggleNotificationsDropdown} className="no-style no-padding no-margin notifications-toggle-button">
					<Icon
						name={ICONS.BELL}
						size={20}
						color={COLORS.WHITE}
					/>
				</button>
				<OptionalComponent display={unseenCount > 0}>
					<div className={classNames("notification-count", { "project-details-enabled": projectDetailsFlagIsOn })}>
						{unseenDisplay}
					</div>
				</OptionalComponent>
				<div
					className={classNames('notification-dropdown', { show: brandliveNotificationsOpen })}
					ref={brandliveNotificationsRef}
				>
					<div className="notification-dropdown-header">
						<h5>Notifications</h5>
						<OptionalComponent display={showActivityLog && currentTab === "Project activity"}>
							<button className="no-style no-padding no-margin" onClick={openActivityLogModal}>
								<GradientText>See all</GradientText>
							</button>
						</OptionalComponent>
					</div>
					<OptionalComponent display={showActivityLog}>
						<div className="notification-tab-header">
							<TabHeader
								tabs={['Updates', 'Project activity']}
								defaultTab="Updates"
								onClick={setCurrentTab}
								currentTab={currentTab}
								activeLineHeight={'3px'}
							/>
						</div>
					</OptionalComponent>

					<div className="admin-notifications-list" onScroll={handleScroll}>
						<SwitchCase input={currentTab}>
							<Case value={"Updates"}>
								<SuperAdminNotifications superAdminNotifications={superAdminNotifications} />
							</Case>
							<Case value={'Project activity'}>
								<ActivityLog
									activityLog={activityLog}
									adminMap={adminMap}
									loadingMore={loadingActivityLog}
								/>
							</Case>
						</SwitchCase>
					</div>
				</div>
			</div >
			<OptionalComponent display={showActivityLog}>
				<ActivityModal
					open={allNotificationsModalOpen}
					onClose={closeActivityLogModal}
					activityLog={activityLog}
					adminMap={adminMap}
					onScrollToBottom={onScrollEnd}
					loadingMore={loadingActivityLog}
					eventUuid={eventUuid}
					ref={activityModalBody}
				/>
			</OptionalComponent>
		</OptionalComponent >
	);
}
